import { get, patch, post, destroy, submitFormData } from "./ServicesApi";
import axios from "axios";

const statusDetail = (id, obj) => {
  const statusPromise = patch(`/v1/event/${id}/status`, obj);
  return statusPromise;
};

const createContract = (body) => {
  const contractsPromise = post("/v1/event", body);
  return contractsPromise;
};

const cloneContract = (id, body) => {
  const clonePromise = post(`/v1/event/${id}/clone`, body);
  return clonePromise;
};

const getContracts = (body) => {
  const contractsPromise = get("/v1/event", { params: body });
  return contractsPromise;
};

const getGraphStats = (body) => {
  const contractsPromise = get("/v1/event/charts/line", { params: body });
  return contractsPromise;
};

const getResourceStats = (body) => {
  const contractsPromise = get("/v1/event/resources", { params: body });
  return contractsPromise;
};

const getParentContractTitles = () => {
  return get("/v1/event/titles");
};

const getContractsChild = (evenId, params) => {
  return get(`/v1/event/${evenId}/children`, { params });
};

const getContractsById = (id, body) => {
  // #369 safety net. A falsy id here builds `GET /v1/event/undefined`, which the
  // API 404s. In prod this fired 529 times in ~15 min from one user's session:
  // a caller (an event-detail data hook keyed on getSummary(mapIndex)?.id) read
  // an id that was not yet available or had been cleared, and a re-render kept
  // re-firing it. Reject at the service boundary so no caller — present or
  // future — can put an undefined id on the wire, no matter the render timing.
  if (id === undefined || id === null || id === "") {
    return Promise.reject(new Error("getContractsById called without a valid id"));
  }
  const contractsPromise = get(`/v1/event/${id}`, { params: body });
  return contractsPromise;
};

// The send dialog holds Send until this answers, so give up quickly and let
// the caller fall back to the original CC list.
const DEFAULT_CC_TIMEOUT_MS = 5000;

/**
 * People the backend says should be CC'd when this event's contract is sent
 * (project-management#269). Resolves to `{ data: { contacts: [{ role, name, email }] } }`.
 * @param {number} eventId - Event ID
 */
const getDefaultCc = (eventId) => {
  if (eventId === undefined || eventId === null || eventId === "") {
    return Promise.reject(new Error("getDefaultCc called without a valid id"));
  }
  return get(`/v1/event/${eventId}/default-cc`, {
    timeout: DEFAULT_CC_TIMEOUT_MS,
  });
};

const updateContract = (id, body) => {
  const updateContractPromise = patch(`/v1/event/${id}`, body);
  return updateContractPromise;
};

/**
 * Updates contract ownership.
 * @param {number} id - Event ID
 * @param {Object} body - Payload: { soldBy?: string, createdBy?: string }
 */
const updateContractOwnership = (id, body) => {
  const updateContractPromise = patch(`/v1/event/${id}/update-ownership`, body);
  return updateContractPromise;
};

const updateContractRegion = (id, body) => {
  const updateContractPromise = patch(`/v1/event/${id}/region`, body);
  return updateContractPromise;
};

const updateContractClientType = (id, body) => {
  const updateContractPromise = patch(`/v1/event/${id}/client-type`, body);
  return updateContractPromise;
};

const deleteContract = async (id) => {
  const deleteContractPromise = await destroy(`/v1/event/${id}`);
  return deleteContractPromise;
};

const getDeletedContracts = (params) => {
  const deletedContracts = get("/v1/event/deleted", { params });
  return deletedContracts;
};

const restoreDeletedContracts = (id) => {
  const restoredContracts = patch(`/v1/event/${id}/restore`);
  return restoredContracts;
};

const uploadPDFContract = async (data) => {
  try {
    const response = await post(`/v2/adobe-sign/upload-and-send`, data);
    return response;
  } catch (error) {
    throw error;
  }
};


const contractPDFStatus = (params) => {
  return get(`/v1/adobe-sign/status`, { params });
};

const contractPDFActivity = (params) => {
  return get(`/v1/adobe-sign/events`, { params });
};

const sendContractReminder = (params) => {
  return get(`/v1/adobe-sign/send-reminder`, { params });
}

const getSignedContract = (params) => {
  // Binary response — without responseType the PDF bytes get mangled by the
  // default JSON transform.
  return get(`/v1/adobe-sign/download-signed-agreement`, {
    params,
    responseType: "blob",
  });
};

// Team-side only: the original with the signatures placed so far, for a
// SealFlow envelope that is still PENDING. Not an executed document.
const getPartiallySignedContract = (params) => {
  return get(`/v1/adobe-sign/download-partially-signed-agreement`, {
    params,
    responseType: "blob",
  });
};

const getSignedContractRedirectURL = (params) => {
  return get(`/v1/adobe-sign/signed-agreement-url`, { params });
};

const cancelAgreement = (params, body) => {
  return patch(`/v1/adobe-sign/cancel-agreement`, body, params);
};

const createContractsTemplate = (data) => {
  return post(`/v1/contract-template`, data);
};

// Rename a template — label only, no version churn (#283).
const renameContractsTemplate = (id, name) => {
  return patch(`/v1/contract-template/${id}/rename`, { name });
};

// Soft-delete a template (#283). Backend blocks Default and anything still
// referenced by a contract/client/event type; the caller surfaces that message.
const deleteContractsTemplate = (id) => {
  return destroy(`/v1/contract-template/${id}`);
};

const getContractsTemplate = (params) => {
  return get(`/v1/contract-template`, { params });
};

// Not to be confused with ContractTemplateServices.js's getContractsTemplates
// (plural, no "List") — that one hits /v1/contract-template/changes (the
// propose/approve audit trail for the ONE template), a different endpoint
// entirely from this one (the actual list of templates, #284).
const getContractsTemplateList = () => {
  return get(`/v1/contract-template/list`);
};

const getContractsTemplateById = (id) => {
  return get(`/v1/contract-template/${id}`);
};

const getContractsTemplateChanges = (params) => {
  return get(`/v1/contract-template/changes`, { params });
};

const getContractsTemplateChangesDetails = (id) => {
  return get(`/v1/contract-template/changes/${id}`);
};

const updateContractsTemplateChangeStatus = (id,status) => {
  return patch(`/v1/contract-template/changes/${id}/${status}`);
};

const updateContractsTemplate = (data) => {
  return patch(`/v1/contract-template`, data);
};

const getDraftContracts = (params) => {
  return get(`/v1/contract-template/changes/draft`, { params });

}


const uploadSignedAgreement = async (pdfFile, eventId) => {
  const formData = new FormData();
  formData.append("pdfFile", pdfFile);
  formData.append("eventId", eventId);

  try {
    const response = await submitFormData(
      `/v1/adobe-sign/upload-signed-agreement`,
      formData
    );
    return response;
  } catch (error) {
    throw error;
  }
};

const updateAgreementParticipant = async ({ contractId, participantSetId, participantEmail }) => {
  try {
    const response = await patch(
      `/v1/adobe-sign/update-agreement-participant?contractId=${contractId}&participantSetId=${participantSetId}`,
      { participantEmail }
    );
    return response;
  } catch (error) {
    throw error;
  }
};

const getAgreementParticipants = (params) => {
  return get(`/v1/adobe-sign/agreement-participants`, { params });
};

const generateTestPDF = async (data) => {
  try {
    // Use axios directly to avoid response interceptor issues with blob responses
    const response = await axios.post(`${process.env.REACT_APP_API_HOST}/v1/pdf/generate`, data, {
      responseType: 'blob',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': localStorage.getItem("token"),
        'ngrok-skip-browser-warning': true
      }
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export {
  statusDetail,
  cancelAgreement,
  sendContractReminder,
  getContractsById,
  getDefaultCc,
  getParentContractTitles,
  updateContractRegion,
  updateContractClientType,
  createContract,
  cloneContract,
  getDraftContracts,
  getSignedContract,
  getPartiallySignedContract,
  updateContract,
  contractPDFActivity,
  updateContractOwnership,
  getContracts,
  getGraphStats,
  getResourceStats,
  getContractsChild,
  deleteContract,
  restoreDeletedContracts,
  getDeletedContracts,
  uploadPDFContract,
  contractPDFStatus,
  getContractsTemplate,
  getContractsTemplateList,
  getContractsTemplateById,
  getContractsTemplateChanges,
  getContractsTemplateChangesDetails,
  updateContractsTemplateChangeStatus,
  createContractsTemplate,
  renameContractsTemplate,
  deleteContractsTemplate,
  updateContractsTemplate,
  getSignedContractRedirectURL,
  uploadSignedAgreement,
  getAgreementParticipants,
  updateAgreementParticipant,
  generateTestPDF,
};
